import { ATTENTION_TIERS, } from "@healthos/shared";
import { z } from "zod";
import { ARMS, armFor } from "./arms.js";
import { DEFAULT_RULE_LIMITS } from "./limits.js";
export const ruleActionSchema = z.enum(ARMS.map((a) => a.action));
export const RULE_ACTIONS = new Set(ruleActionSchema.options);
export const CAPABILITIES = ARMS.map((a) => a.capability);
export const DAILY_BUDGET = DEFAULT_RULE_LIMITS.dailyBudget;
/** An arm nobody has fired reads as this long unseen, so the comparison stays finite. */
const UNSEEN_MS = 30 * 24 * 60 * 60 * 1000;
/** What a rung spends. A rung is what the moment earned, so the cost belongs to it rather than to each arm standing on it. */
const RUNG_COST = { 1: 1, 2: 2, 3: 2 };
export function canFire(rung, spent, dailyBudget) {
    return spent + RUNG_COST[rung] <= dailyBudget;
}
export function spendOf(rung) {
    return RUNG_COST[rung];
}
function inRefractory(capability, refractory) {
    if (!refractory)
        return false;
    const last = refractory.lastFiredAt.get(capability);
    if (!last)
        return false;
    const arm = armFor(capability);
    const windowMs = arm.refractoryHours === undefined ? refractory.windowMs : arm.refractoryHours * 3_600_000;
    return refractory.now.getTime() - last.getTime() < windowMs;
}
/** The behaviour a capability acts on, via the arm library that owns it. */
function behaviourOf(capability) {
    return armFor(capability).pillar;
}
/** The one place a candidate becomes the moment: */
export function chooseCandidate(candidates, opts) {
    /**
     * How loudly this arm is allowed to speak, ranked by the shared order so a
     * tier added there needs no change here. Beneath rung deliberately: rung is
     * what the moment earned, tier only how urgently to say it, and letting tier
     * jump the queue would let a routine arm outrank an earned one.
     */
    const tierRank = (capability) => ATTENTION_TIERS.indexOf(armFor(capability).tier);
    /** Rung first, then how loudly it may speak, then the member's own evidence. */
    const strengthOf = (capability) => opts.tailoring?.find((t) => t.behaviour === behaviourOf(capability))?.strength ?? 0;
    const goalRank = (capability) => opts.goal && behaviourOf(capability) === opts.goal.behaviour ? 1 : 0;
    // Below the goal and above the correlation:
    const offUsualRank = (capability) => opts.offUsual?.includes(behaviourOf(capability)) ? 1 : 0;
    // A reading off its band today outranks a week drifting, which outranks a correlation.
    const slippingRank = (capability) => opts.slipping?.includes(behaviourOf(capability)) ? 1 : 0;
    /** Among options the situation has earned equally, the one the member has not seen lately. */
    const sinceLastFire = (capability) => {
        const last = opts.refractory?.lastFiredAt.get(capability);
        if (!last || !opts.refractory)
            return UNSEEN_MS;
        return Math.min(UNSEEN_MS, opts.refractory.now.getTime() - last.getTime());
    };
    const ranked = candidates
        .filter((capability) => opts.allowedFamilies === undefined ||
        opts.allowedFamilies.includes(behaviourOf(capability)))
        .filter((capability) => !inRefractory(capability, opts.refractory))
        .sort((a, b) => {
        const rung = armFor(b).rung - armFor(a).rung;
        if (rung !== 0)
            return rung;
        const tier = tierRank(b) - tierRank(a);
        if (tier !== 0)
            return tier;
        const goal = goalRank(b) - goalRank(a);
        if (goal !== 0)
            return goal;
        const off = offUsualRank(b) - offUsualRank(a);
        if (off !== 0)
            return off;
        const slip = slippingRank(b) - slippingRank(a);
        if (slip !== 0)
            return slip;
        const strength = strengthOf(b) - strengthOf(a);
        if (strength !== 0)
            return strength;
        return sinceLastFire(b) - sinceLastFire(a);
    });
    const capability = ranked.find((c) => canFire(armFor(c).rung, opts.spent, opts.limits.dailyBudget));
    if (capability === undefined)
        return null;
    const behaviour = behaviourOf(capability);
    const tailoredBy = opts.tailoring?.find((t) => t.behaviour === behaviour) ?? null;
    const goalMatched = opts.goal?.behaviour === behaviour ? opts.goal : null;
    return { capability, tailoredBy, goalMatched };
}
//# sourceMappingURL=ladder.js.map